const TABBABLE_NODES = /input|select|textarea|button|object/;
export const FOCUS_SELECTOR = 'a, input, select, textarea, button, object, [tabindex]';

/**
 * Checks if an element is hidden or not.
 * An element is considered hidden if its display style is set to 'none'.
 *
 * @param {HTMLElement} element - The HTML element to check.
 * @returns {boolean} - Returns true if the element is hidden, false otherwise.
 */
function hidden(element: HTMLElement): boolean {
  return element.style.display === 'none';
}

/**
 * Checks if an element is visible.
 * An element is considered visible if it is not hidden and all its parent elements are also not hidden.
 *
 * @param {HTMLElement} element - The HTML element to check.
 * @returns {boolean} - Returns true if the element is visible, false otherwise.
 */
function visible(element: HTMLElement): boolean {
  const isHidden =
    element.getAttribute('aria-hidden') ||
    element.getAttribute('hidden') ||
    element.getAttribute('type') === 'hidden';

  if (isHidden) {
    return false;
  }

  let parentElement: HTMLElement = element;
  while (parentElement) {
    // If the parent is the body or a shadow root, we stop checking further
    if (parentElement === document.body || parentElement.nodeType === 11) {
      break;
    }

    if (hidden(parentElement)) {
      return false;
    }

    parentElement = parentElement.parentNode as HTMLElement;
  }

  return true;
}

/**
 * Gets the tabindex of an element.
 * If the tabindex attribute is not set, it returns undefined.
 * If the tabindex is set to a non-numeric value, it returns NaN.
 *
 * @param {HTMLElement} element - The HTML element to get the tabindex from.
 * @returns {number} - Returns the tabindex as a number, or NaN if not set.
 */
function getElementTabIndex(element: HTMLElement): number {
  let tabIndex: string | null | undefined = element.getAttribute('tabindex');
  if (tabIndex === null) {
    tabIndex = undefined;
  }
  return parseInt(tabIndex as string, 10);
}

/**
 * Checks if an element is focusable.
 *
 * @param {HTMLElement} element - The HTML element to check.
 * @returns {boolean} - Returns true if the element is focusable, false otherwise.
 */
export function focusable(element: HTMLElement): boolean {
  const nodeName = element.nodeName.toLowerCase();
  const isTabIndexNotNaN = !Number.isNaN(getElementTabIndex(element));
  const res =
    // @ts-expect-error function accepts any html element but if it is a button, it should not be disabled to trigger the condition
    (TABBABLE_NODES.test(nodeName) && !element.disabled) ||
    (element instanceof HTMLAnchorElement ? element.href || isTabIndexNotNaN : isTabIndexNotNaN);

  return res && visible(element);
}

/**
 * Checks if an element is tabbable.
 * An element is tabbable if it is focusable and its tabindex is either not set or greater than or equal to 0.
 *
 * @param {HTMLElement} element - The HTML element to check.
 * @returns {boolean} - Returns true if the element is tabbable, false otherwise.
 */
export function tabbable(element: HTMLElement): boolean {
  const tabIndex = getElementTabIndex(element);
  const isTabIndexNaN = Number.isNaN(tabIndex);
  return (isTabIndexNaN || tabIndex >= 0) && focusable(element);
}

/**
 * Finds all tabbable descendants of a given element.
 * It uses the FOCUS_SELECTOR to select elements and filters them using the tabbable function.
 *
 * @param {HTMLElement} element - The HTML element to search within.
 * @returns {HTMLElement[]} - An array of tabbable descendant elements.
 */
export function findTabbableDescendants(element: HTMLElement): HTMLElement[] {
  return Array.from(element.querySelectorAll<HTMLElement>(FOCUS_SELECTOR)).filter(tabbable);
}
